import { CONFIGURATION } from "@revolt/common";

/**
 * Silent OIDC session-consistency check (Whitemane fork only).
 *
 * The chat session and the portal (IdP) session are independent: after SSO
 * login the chat keeps its own long-lived token, so switching accounts on the
 * id portal does not switch the chat. To make the chat follow the portal
 * identity in the same browser, we remember the IdP `sub` the session was
 * minted for (delivered in the /login/callback fragment) and, on startup,
 * ask app-api who the portal session currently belongs to.
 *
 * Outcomes:
 *  - portal says a DIFFERENT account -> "mismatch" (caller logs out and
 *    re-enters the SSO flow, which silently signs into the new account)
 *  - portal agrees, has no session (401), the check is not configured, or
 *    anything fails -> "ok" (an expired portal session must NOT log the
 *    chat out; the long-lived chat session is the intended behavior)
 */

const SUB_STORAGE_KEY = "wm-oidc-sub";

/** Remember which IdP account (sub) the current chat session was minted for. */
export function storeOidcSub(sub: string) {
  try {
    localStorage.setItem(SUB_STORAGE_KEY, sub);
  } catch {
    // Storage unavailable (private mode quota etc.) - the check just won't run.
  }
}

/** Forget the stored sub (on logout). */
export function clearOidcSub() {
  try {
    localStorage.removeItem(SUB_STORAGE_KEY);
  } catch {
    // ignore
  }
}

/**
 * Explicit-logout marker (per tab). /login auto-enters the SSO flow in OIDC
 * mode; after a deliberate logout that would instantly sign the user back in
 * (the portal session is long-lived), so logout sets this flag and /login
 * consumes it to show the sign-in button once instead of redirecting.
 */
const LOGOUT_FLAG_KEY = "wm-explicit-logout";

/** Set by logout so the next /login render does not auto-enter SSO. */
export function markExplicitLogout() {
  try {
    sessionStorage.setItem(LOGOUT_FLAG_KEY, "1");
  } catch {
    // ignore
  }
}

/** Read-and-clear the explicit-logout flag. */
export function consumeExplicitLogout(): boolean {
  try {
    const flagged = sessionStorage.getItem(LOGOUT_FLAG_KEY) !== null;
    sessionStorage.removeItem(LOGOUT_FLAG_KEY);
    return flagged;
  } catch {
    return false;
  }
}

/**
 * One-shot guard (per tab) for the account-follow redirect. The consistency
 * check sends the browser through the SSO round-trip to switch accounts; a
 * sub that never reconciles with the portal would otherwise redirect on every
 * startup. The flag bounds it to a single redirect and is cleared once a load
 * comes back consistent, so a later genuine switch still redirects once.
 */
const SWITCH_FLAG_KEY = "wm-oidc-switch";

/** Set before the account-follow SSO redirect. */
export function markOidcSwitchAttempted() {
  try {
    sessionStorage.setItem(SWITCH_FLAG_KEY, "1");
  } catch {
    // ignore
  }
}

/** Whether the account-follow redirect already fired this tab session. */
export function oidcSwitchAttempted(): boolean {
  try {
    return sessionStorage.getItem(SWITCH_FLAG_KEY) !== null;
  } catch {
    return false;
  }
}

/** Clear the guard once a startup load is account-consistent. */
export function clearOidcSwitchAttempt() {
  try {
    sessionStorage.removeItem(SWITCH_FLAG_KEY);
  } catch {
    // ignore
  }
}

/**
 * Compare the portal's active account against the stored sub.
 * Resolves to "mismatch" only on a confirmed different account.
 */
export async function checkOidcSessionConsistency(): Promise<
  "ok" | "mismatch"
> {
  if (!CONFIGURATION.OIDC_LOGIN || !CONFIGURATION.OIDC_ACCOUNT_API_URL)
    return "ok";

  let stored: string | null = null;
  try {
    stored = localStorage.getItem(SUB_STORAGE_KEY);
  } catch {
    return "ok";
  }

  // Sessions created before this feature have no stored sub - leave them be.
  if (!stored) return "ok";

  try {
    const response = await fetch(
      `${CONFIGURATION.OIDC_ACCOUNT_API_URL}/v1/account/me`,
      { credentials: "include" },
    );

    // 401 = no/expired portal session; that is fine, the chat session stands.
    if (!response.ok) return "ok";

    const body = (await response.json()) as { id?: string };
    if (!body.id) return "ok";

    return body.id === stored ? "ok" : "mismatch";
  } catch {
    // Network/CORS failure - never punish the chat session for it.
    return "ok";
  }
}
